import React, { useEffect } from "react";
import * as Sentry from "@sentry/react";
import type { User } from "@domain/Auth";
import { browserTokenStore } from "@gui/infrastructure/pass-through/storage";
import { setAuthFailureHandler } from "@gui/infrastructure/pass-through/api";
import { API } from "@gui/infrastructure/pass-through/api";
import { sentryUserScope } from "@gui/infrastructure/pass-through/sentryScope";
import { useImpersonationContext } from "@gui/contexts/impersonation";

export interface UseCurrentUser {
	/** The currently authenticated user, or undefined while loading / when logged out. */
	currentUser: User | undefined;
	/** True while the initial user fetch is in flight (or while a child route is loading). */
	loading: boolean;
	/** Set the user directly — used by Login/Register/PasswordReset views after a successful auth. */
	setCurrentUser: React.Dispatch<React.SetStateAction<User | undefined>>;
	/** Set the loading flag — used by route-level views (JobsDashboard, MarkJobReadyPage) during their own data fetches. */
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Fetch + maintain the current authenticated user.
 *
 * Owns three coupled effects:
 * 1. Wires the API auth-failure interceptor to clear `currentUser` on 401.
 * 2. On token presence (and on impersonation change), fetches `API.Users.me()`.
 *    On 403 + active impersonation, revokes the impersonation and lets the next
 *    cycle re-fetch as the real user.
 * 3. Pushes the resolved user into Sentry's user, tags and context for error attribution.
 */
export function useCurrentUser(): UseCurrentUser {
	const [currentUser, setCurrentUser] = React.useState<User | undefined>();
	const [loading, setLoading] = React.useState(true);
	const { impersonatedEmail, stopImpersonation } = useImpersonationContext();

	// Register auth-failure handler so a 401 from the interceptor clears the user state.
	useEffect(() => {
		setAuthFailureHandler(() => setCurrentUser(undefined));
	}, []);

	useEffect(() => {
		const token = browserTokenStore.getJwt();
		if (!token) {
			setLoading(false);
			return;
		}
		setCurrentUser(undefined);
		setLoading(true);
		const fetchUser = async () => {
			try {
				const user = await API.Users.me();
				setCurrentUser(user);
			} catch (error: any) {
				// 401 is handled by the axios interceptor (clears tokens + user state).
				// Only handle 403 impersonation case here.
				const status = error?.response?.status;
				if (status === 403 && impersonatedEmail) {
					stopImpersonation();
				}
			} finally {
				setLoading(false);
			}
		};
		fetchUser();
	}, [impersonatedEmail]);

	// Push resolved user into Sentry so error reports carry identity, role, company and
	// impersonation (attributed to the developer, not the impersonated account).
	useEffect(() => {
		if (!currentUser) return;
		const scope = sentryUserScope(currentUser, impersonatedEmail, browserTokenStore.getJwt());
		Sentry.setUser(scope.user);
		Sentry.setTags(scope.tags);
		Sentry.setContext("portal_user", scope.context);
	}, [currentUser, impersonatedEmail]);

	return { currentUser, loading, setCurrentUser, setLoading };
}
